import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { lineText } from '../shell/ActionIcons';

// Reusable "action panel" dismiss standard (mirrors the shell ModuleAlert):
//   • the panel collapses IN PLACE (chevron), and
//   • a "Got it" fully hides it and drops a chip next to the Guide button (#notice-chips);
//     clicking the chip brings the panel back.
// Both states persist per id (localStorage). Returns helpers the panel renders itself, so it keeps
// its own look — this only owns the dismissed/collapsed state + the Guide-adjacent chip.
const read = (k, fb) => { try { const v = localStorage.getItem(k); return v == null ? fb : JSON.parse(v); } catch { return fb; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota */ } };

export function useDismissible(id, chipLabel, chipTone = '', chipStyle = null) {
  const dkey = `panel.${id}.dismissed`, ckey = `panel.${id}.collapsed`;
  const [dismissed, setDismissed] = useState(() => read(dkey, false));
  const [collapsed, setCollapsed] = useState(() => read(ckey, false));
  const [slot, setSlot] = useState(null);
  useEffect(() => { setSlot(document.getElementById('notice-chips')); }, [dismissed]);
  useEffect(() => write(dkey, dismissed), [dismissed, dkey]);
  useEffect(() => write(ckey, collapsed), [collapsed, ckey]);

  const chip = dismissed && slot
    ? createPortal(
        <button type="button" className={`notice-chip ${chipTone}`} style={chipStyle} onClick={() => setDismissed(false)} title="Show panel">{lineText(chipLabel)}</button>,
        slot)
    : null;

  return {
    dismissed, collapsed,
    toggleCollapse: () => setCollapsed(c => !c),
    dismiss: () => setDismissed(true),
    chip,   // render this anywhere; it portals to the Guide-adjacent slot
  };
}

// Small standard header controls a panel drops into its own title row: a ▾/▸ collapse chevron and a "Got it".
export function PanelControls({ collapsed, onToggle, onDismiss, color = '#8a5a08', bg = 'rgba(196,125,16,.14)' }) {   // fb-105: bg overridable (green panels want a non-warning tint)
  return (
    <>
      <button type="button" onClick={onToggle} title={collapsed ? 'Expand' : 'Collapse'} className="btn sm">{collapsed ? '▸' : '▾'}</button>
      <span style={{ flex: 1 }} />
      {/* fb-105: fully self-styled — the .gotit CSS only applies inside a .callout, so panels outside one need inline styling. */}
      <button className="gotit" type="button" onClick={onDismiss}
        style={{ background: bg, color }}>Got it</button>
    </>
  );
}
